import { NgModule } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CookieService } from 'ngx-cookie-service';
import { FullscreenOverlayContainer, OverlayContainer, OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { MaterialModule } from './material.module';
import { LoadingSpinnerComponent } from './components/loading-spinner/loading-spinner.component';
import { LoadingDirective } from './directives/loading.directive';
import { ModalComponent } from './components/modal/modal.component';
import { ModalService } from './helpers/modal.service';
import { StatusIndicatorComponent } from './components/status-indicator/status-indicator.component';
import { WizardComponent } from './components/wizard/wizard.component';
import { ModalConfirmComponent } from './components/modal-confirm/modal-confirm.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { RouterModule } from '@angular/router';
import { FileManagerService } from './helpers/file-manager.service';
import { ServiceAccountDirective } from './directives/service-account.directive';
import { DynamicComponentDirective } from './directives/dynamic-component.directive';
import { OnlyNumberDirective } from './directives/only-number.directive';
import { AutofocusDirective } from './directives/autofocus.directive';
import { PageTitleComponent } from './components/page-title/page-title.component';
import { PrimaryButtonDirective } from './directives/primary-button.directive';
import { ImagePickerComponent } from './components/image-picker/image-picker.component';
import { ImageSnippetService } from './helpers/image-snippet.service';
import { TableComponent } from './components/table/table.component';
import { CategoryIndicatorComponent } from './components/category-indicator/category-indicator.component';
import { ColorService } from './helpers/color.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { SecondaryButtonDirective } from './directives/secondary-button.directive';
import { DateService } from './helpers/date.service';
import { EmptyStateComponent } from './components/empty-state/empty-state.component';
import { FormatPercentagePipe } from './pipes/format-percentage.pipe';
import { EllipsifyContentDirective } from './directives/ellipsify-content.directive';
import { ExportTableService } from './helpers/export-table.service';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { TranslateModule } from '@app/shared/translate.module';
import { LanguageSelectComponent } from '@app/shared/components/language-select/language-select.component';
import { FileUploadComponent } from './components/file-upload/file-upload.component';
import { FileDragAndDropDirective } from './directives/file-drag-and-drop.directive';
import { ValuesParserService } from '@app/shared/helpers/values-parser.service';
import { ImageService } from '@app/shared/helpers/image.service';
import { SelectableCardDirective } from '@app/shared/directives/selectable-card.dirtective';
import { PermissionsDirective } from '@app/shared/directives/permissions.directive';
import { TreeViewComponent } from './components/tree-view/tree-view.component';
import { TreeViewDragDirective } from './directives/tree-view-drag.directive';
import { DeviceRendererDirective } from './directives/device-renderer.directive';
import { DeviceService } from '@app/shared/helpers/device.service';
import { MultipleSelectionListComponent } from '@app/shared/components/multiple-selection-list/multiple-selection-list.component';
import { MultipleSelectionTreeComponent } from './components/multiple-selection-tree/multiple-selection-tree.component';
import { CdkMenuModule } from '@angular/cdk/menu';
import { PortalModule } from '@angular/cdk/portal';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { DialogModule } from '@angular/cdk/dialog';
import { ContextMenuDirective } from '@app/shared/directives/context-menu.directive';
import { NotificationComponent } from './components/notification/notification.component';
import { SearchInputDirective } from '@app/shared/directives/search-input.directive';
import { RoundInputValueDirective } from '@app/shared/directives/round-input-value.directive';
import { HelpArticleRendererDirective } from '@app/shared/directives/help-article-renderer.directive';
import { EnableAdminOnlyDirective } from '@app/shared/directives/enable-admin-only.directive';
import { AdminWrapperComponent } from './components/admin-wrapper/admin-wrapper.component';
import { PdfViewerComponent } from './components/pdf-viewer/pdf-viewer.component';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { DynamicFormComponent } from './components/dynamic-form/dynamic-form.component';
import { DynamicFormFieldComponent } from './components/dynamic-form-field/dynamic-form-field.component';
import { DynamicFormService } from '@app/shared/helpers/dynamic-form.service';
import { StringArrayToStringPipe } from '@app/shared/pipes/string-array-to-string.pipe';
import { AssetGroupsHelperService } from '@app/shared/helpers/asset-groups-helper.service';
import { CamelCaseToStringPipe } from '@app/shared/pipes/camel-case-to-string.pipe';
import { TreeViewSelectComponent } from '@app/shared/components/tree-view-select/tree-view-select.component';
import { HelpArticleDirective } from '@app/shared/directives/help-article.directive';
import { FeatureDiscoveryComponent } from './components/feature-discovery/feature-discovery.component';
import { FeatureDiscoveryDirective } from './directives/feature-discovery.directive';
import { AssetDrillDownComponent } from './components/asset-drill-down/asset-drill-down.component';
import { AssetDrillDownHelperService } from '@app/shared/helpers/asset-drill-down-helper.service';
import { AutocompleteComponent } from '@app/shared/components/autocomplete/autocomplete.component';
import { DateTimePickerComponent } from '@app/shared/components/date-time-picker/date-time-picker.component';
import { MouseEventsDirective } from '@app/shared/directives/mouse-events.directive';
import { ExpandableCardComponent } from './components/expandable-card/expandable-card.component';
import { ToolbarButtonComponent } from './components/toolbar-button/toolbar-button.component';
import { SafeUrlPipe } from './pipes/safe-url.pipe';
import { RedirectingLoaderComponent } from './components/redirecting-loader/redirecting-loader.component';

const directives = [
    AutofocusDirective,
    ContextMenuDirective,
    DeviceRendererDirective,
    DynamicComponentDirective,
    EllipsifyContentDirective,
    EnableAdminOnlyDirective,
    FeatureDiscoveryDirective,
    FileDragAndDropDirective,
    HelpArticleRendererDirective,
    LoadingDirective,
    MouseEventsDirective,
    OnlyNumberDirective,
    PermissionsDirective,
    PrimaryButtonDirective,
    RoundInputValueDirective,
    SearchInputDirective,
    SecondaryButtonDirective,
    SelectableCardDirective,
    ServiceAccountDirective,
];

const components = [
    AdminWrapperComponent,
    AutocompleteComponent,
    AssetDrillDownComponent,
    CategoryIndicatorComponent,
    DateTimePickerComponent,
    DynamicFormComponent,
    DynamicFormFieldComponent,
    EmptyStateComponent,
    ExpandableCardComponent,
    FeatureDiscoveryComponent,
    FileUploadComponent,
    ImagePickerComponent,
    LanguageSelectComponent,
    LoadingSpinnerComponent,
    ModalComponent,
    ModalConfirmComponent,
    MultipleSelectionListComponent,
    MultipleSelectionTreeComponent,
    NotificationComponent,
    PageTitleComponent,
    PdfViewerComponent,
    RedirectingLoaderComponent,
    SidebarComponent,
    StatusIndicatorComponent,
    TableComponent,
    ToolbarButtonComponent,
    TreeViewComponent,
    TreeViewDragDirective,
    TreeViewSelectComponent,
    WizardComponent,
];

const pipes = [CamelCaseToStringPipe, FormatPercentagePipe, SafeUrlPipe, StringArrayToStringPipe];

const services = [
    AssetGroupsHelperService,
    AssetDrillDownHelperService,
    ColorService,
    CookieService,
    DateService,
    DeviceService,
    DynamicFormService,
    ExportTableService,
    FileManagerService,
    ImageService,
    ImageSnippetService,
    ModalService,
    ValuesParserService,
];

@NgModule({
    declarations: [...pipes, ...directives, ...components],
    imports: [
        CdkMenuModule,
        CommonModule,
        DialogModule,
        DragDropModule,
        FormsModule,
        InfiniteScrollModule,
        MaterialModule,
        NgxExtendedPdfViewerModule,
        NgxSkeletonLoaderModule,
        OverlayModule,
        PortalModule,
        ReactiveFormsModule,
        RouterModule,
        ScrollingModule,
        TranslateModule,
        HelpArticleDirective,
    ],
    exports: [
        CdkMenuModule,
        DialogModule,
        DragDropModule,
        FormsModule,
        InfiniteScrollModule,
        MaterialModule,
        NgxExtendedPdfViewerModule,
        NgxSkeletonLoaderModule,
        OverlayModule,
        PortalModule,
        ReactiveFormsModule,
        ScrollingModule,
        TranslateModule,
        ...pipes,
        ...directives,
        ...components,
    ],
    providers: [...services, { provide: OverlayContainer, useClass: FullscreenOverlayContainer }],
})
export class SharedModule {}
